import { Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ContadorContextoState } from '../../models/contador/contador-operar.model';
import { ClienteContadorConsultaDTO } from '../../models/contador/contador.model';

/**
 * Estado em memoria do cliente sob o qual o contador esta operando.
 *
 * Fonte UNICA do valor do header `X-Empresa-Contexto` (lido pelo
 * `ContadorContextoInterceptor` a cada requisicao). Comeca VAZIO (`null`):
 * sem cliente selecionado, o header nao e enviado em nenhuma chamada.
 *
 * Seguranca de escopo (ver DESIGN_FE §"Segurança e Escopo"):
 * - estado inicia `null`;
 * - so o `OperarClienteComponent` chama `selecionar`/`limpar`;
 * - o contexto SOBREVIVE a navegacao APENAS entre as telas que operam sob ele
 *   (operar-cliente e a fatura aberta a partir dela); ao navegar para QUALQUER
 *   outra rota, o contexto e limpo em `NavigationStart` (antes do componente
 *   alvo carregar — fecha qualquer janela de vazamento);
 * - limpeza deterministica tambem em: troca de cliente e 403 de contexto.
 *
 * `providedIn: 'root'`: o interceptor e singleton global e precisa injetar a
 * mesma instancia deste service.
 */
@Injectable({ providedIn: 'root' })
export class ContadorContextoService {
  /** Prefixos de rota onde o contexto do contador permanece ativo. */
  private static readonly ROTAS_CONTEXTO: ReadonlyArray<string> = [
    '/contador/operar',
    '/notas-list/fatura',
  ];

  private readonly clienteSubject = new BehaviorSubject<ContadorContextoState | null>(null);

  /** Observable do cliente ativo (para a UI reagir). */
  readonly cliente$: Observable<ContadorContextoState | null> = this.clienteSubject.asObservable();

  constructor(private router: Router) {
    // Limpa o contexto ao navegar para fora das telas que operam sob contexto.
    // NavigationStart dispara ANTES do componente alvo carregar, garantindo que
    // nenhuma requisicao da nova tela leve o header indevidamente.
    this.router.events
      .pipe(filter((e): e is NavigationStart => e instanceof NavigationStart))
      .subscribe((e) => {
        if (this.clienteAtual() && !ContadorContextoService.rotaPermiteContexto(e.url)) {
          this.limpar();
        }
      });
  }

  /** True quando a URL alvo e uma das telas que operam sob contexto. */
  private static rotaPermiteContexto(url: string): boolean {
    const path = url.split('?')[0];
    return ContadorContextoService.ROTAS_CONTEXTO.some((prefixo) => path.startsWith(prefixo));
  }

  /** Ativa o contexto sobre um cliente (ao selecionar/trocar). */
  selecionar(cliente: ClienteContadorConsultaDTO): void {
    this.clienteSubject.next({
      clienteEmpresaId: cliente.empresaIndicadaId,
      nomeCliente: cliente.nome,
    });
  }

  /** Limpa o contexto (sair da tela / 403 / sem cliente). O header deixa de ser enviado. */
  limpar(): void {
    this.clienteSubject.next(null);
  }

  /** Leitura SINCRONA do cliente atual (usada pelo interceptor a cada request). */
  clienteAtual(): ContadorContextoState | null {
    return this.clienteSubject.value;
  }
}
